import { Component, ElementRef, ViewChild, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NodeValidationService, ValidationResult } from './node-validation.service';
import { IStateNode } from '../../Models';
import { ModuleService } from '../../Services/module.service';
import { TenantService } from '../../../../../Services/tenant.service';
import { FormsModule } from '@angular/forms';

declare var vis: any;

interface Node {
  Id: number;
  Name: string;
  label: string;
  ModuleUniqueId: string;
  Version: number;
  ModuleStatus_Id: number;
  ModuleStatus_Description: string;
  GlobalStatus_Description: string;
  IsInitialState: boolean;
  IsSignOffState: boolean;
  IsPublishable: boolean;
  Steps: string;
  Fields: string;
  RequiredPermissions: string;
}

interface Edge {
  Id: number;
  from: number;
  to: number;
  Name: string;
  label: string;
  Description: string;
  FromStateName: string;
  ToStateName: string;
  IsApprovalTransition: boolean;
  IsRejectionTransition: boolean;
  SaveButton_Label: string;
  SaveButton_Enabled: boolean;
  SaveButton_IsUnlock: boolean;
  SaveButton_MoveNext: boolean;
  SaveButton_Style: string;
  HasSystemRules: boolean;
  MandatoryFields: string;
  PreConditionRules: string;
  PostConditionValues: string;
  RequiredPermissions: string;
  TriggerFields: string;
  systemRules: any[];
}

@Component({
    selector: 'aw-node-graph',
    templateUrl: 'node-graph.component.html',
    styleUrls: ['node-graph.component.scss'],
    imports: [FormsModule]
})
export class NodeGraphComponent implements OnInit, OnDestroy, OnChanges {
  @Input() stateData: IStateNode[] = [];
  @ViewChild('networkContainer', { static: true }) networkContainer!: ElementRef;
  
  private readonly fallbackEdgeIdBase = 1000000000;
  private network: any = null;
  
  isLoading = false;
  errorMessage: string | null = null;
  nodeCount = 0;
  edgeCount = 0;
  hasData = false;
  currentModuleId: string = '';
  private physicsEnabled = true;
  private networkInitialized = false;
  private lastDataHash: string = '';
  private updateTimeout: any = null;
  
  // Edge filtering
  showAllEdges = false;
  hideUnlockedEdges = true;
  hideSpecificRelationships = false;
  hideDeletedRelationships = true;
  
  // Store IDs of states that are "deleted" states for semantic filtering
  private deletedStateIds = new Set<number>();
  
  // Selection state
  selectedNode: any = null;
  selectedEdge: any = null;
  nodeSteps: any[] = [];
  nodeFields: any[] = [];
  edgeRules: any[] = [];
  nodeValidation: ValidationResult | null = null;
  showSidebar = false;
  
  // Store current edges for validation and node validation status
  private currentEdges: any[] = [];
  private nodeValidationStatus = new Map<number, boolean>(); // nodeId -> canProgress
  private validationTimeout: any = null;
  
  // Validation feedback
  validationInProgress = false;
  dynamicRulesLoaded = false;
  validationStatus = '';
  
  // Note: SQL Script Generation features removed as they require database write access
  
  constructor(
    private validationService: NodeValidationService,
    private route: ActivatedRoute,
    private moduleService: ModuleService,
    private tenantService: TenantService
  ) {}

  async ngOnInit() {
    this.initializeNetwork();
    
    // If stateData is provided as input, use it
    if (this.stateData && this.stateData.length > 0) {
      this.processStateData();
    } else {
      // Otherwise, load data from route parameters (when accessed directly)
      await this.loadStateFromRoute();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.stateData && !changes.stateData.firstChange) {
      // Force reprocessing by clearing the hash since the data reference may have changed
      this.lastDataHash = '';
      this.processStateData();
    }
  }
  
  get validationAvailable(): boolean {
    return !this.showAllEdges && (this.hideUnlockedEdges && this.hideDeletedRelationships && !this.hideSpecificRelationships)
  }

  processStateData() {
    if (this.stateData.length === 0) {
      this.hasData = false;
      return;
    }

    // Create a hash of the data to detect changes
    const dataHash = JSON.stringify(this.stateData.map(s => ({ id: s.ModuleStatusId, name: s.Name, transitions: s.StateTransitions?.length || 0 })));
    
    this.lastDataHash = dataHash;
    
    // Clear any pending update
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    // Debounce the update
    this.updateTimeout = setTimeout(() => {
      
      // Convert IStateNode[] to Node[] and Edge[]
      const nodes = this.convertStateNodesToNodes(this.stateData);
      const edges = this.convertStateNodesToEdges(this.stateData);
      
      this.updateGraph(nodes, edges);
      this.nodeCount = nodes.length;
      this.edgeCount = edges.length;
      this.hasData = true;
      
      // Set up validation service with first module found
      if (this.stateData.length > 0) {
        // Only update currentModuleId if it's not already set (from route loading)
        if (!this.currentModuleId) {
          this.currentModuleId = this.stateData[0].ModuleId;
        }
        
        // Always update validation service with current module
        this.validationService.setCurrentModule(this.currentModuleId);
        
        // Inject services for dynamic rule loading
        this.validationService.setServices(this.moduleService, this.tenantService);
      }
      
      this.updateTimeout = null;
    }, 100); // 100ms debounce
  }

  private initializeNetwork() {
    if (this.networkInitialized) return;
    
    const container = this.networkContainer.nativeElement;
    
    const data = {
      nodes: [],
      edges: []
    };
    
    const options = {
      nodes: {
        shape: 'box',
        size: 20,
        font: {
          size: 14,
          color: '#ffffff',
          face: 'Arial'
        },
        borderWidth: 2,
        shadow: true,
        margin: {
          top: 10,
          right: 10,
          bottom: 10,
          left: 10
        },
        widthConstraint: {
          minimum: 100,
          maximum: 200
        }
      },
      edges: {
        width: 2,
        color: { inherit: 'from' },
        smooth: {
          enabled: true,
          type: 'continuous',
          roundness: 0.5
        },
        arrows: {
          to: { enabled: true, scaleFactor: 1, type: 'arrow' }
        }
      },
      physics: {
        enabled: true,
        stabilization: { 
          enabled: true,
          iterations: 100,
          onlyDynamicEdges: false
        },
        barnesHut: {
          gravitationalConstant: -80000,
          springConstant: 0.001,
          springLength: 200
        }
      },
      interaction: {
        hover: true,
        selectConnectedEdges: false
      }
    };
    
    this.network = new vis.Network(container, data, options);
    this.networkInitialized = true;
    
    // Disable physics after initial stabilization to prevent nodes from moving
    this.network.once('stabilizationIterationsDone', () => {
      if (this.physicsEnabled && this.network) {
        this.network.setOptions({ physics: { enabled: false } });
        this.physicsEnabled = false;
      }
    });
    
    // Add event listeners
    this.network.on('click', (event) => {
      if (event.nodes.length > 0) {
        const nodeId = event.nodes[0];
        this.selectNode(nodeId);
      } else if (event.edges.length > 0) {
        const edgeId = event.edges[0];
        this.selectEdge(edgeId);
      } else {
        this.closeSidebar();
      }
    });
  }

  async loadStateFromRoute() {
    try {
      this.isLoading = true;
      this.errorMessage = null;
      
      // Get route parameters
      const moduleId = this.route.parent?.snapshot.params['moduleId'];
      const version = parseInt(this.route.parent?.snapshot.params['version'] || '1');
      
      
      if (!moduleId) {
        this.errorMessage = 'Module ID not found in route parameters';
        return;
      }
      
      // Get current tenant
      const tenant = this.tenantService.selectedTenant;
      if (!tenant) {
        this.errorMessage = 'No tenant selected';
        return;
      }
      
      
      // Check if module has changed
      const moduleChanged = this.currentModuleId && this.currentModuleId !== moduleId;
      if (moduleChanged) {
        this.validationService.clearCache();
        this.nodeValidationStatus.clear(); // Clear old validation status
      }
      
      // Load module content first to have fields available
      await this.moduleService.loadModuleContent(tenant, moduleId, version);
      
      // Load full state data using module service - this gets all the state transitions with rule IDs
      await this.moduleService.loadModuleStateContent(tenant, moduleId, version);
      
      // Also load the actual state rules data from tenant service
      await this.tenantService.loadStateRules();
      
      if (this.moduleService.moduleStateContent && this.moduleService.moduleStateContent.length > 0) {
        this.stateData = this.moduleService.moduleStateContent;
        
        // Update current module ID BEFORE processing state data
        this.currentModuleId = moduleId;
        
        this.processStateData();
      } else {
        this.errorMessage = 'No state data found for this module and version';
      }
      
    } catch (error) {
      this.errorMessage = `Failed to load state data: ${error}`;
    } finally {
      this.isLoading = false;
    }
  }

  refreshData() {
    if (this.stateData && this.stateData.length > 0) {
      this.processStateData();
    } else {
      this.loadStateFromRoute();
    }
  }

  onEdgeFilterChange() {
    // Re-render the graph with filtered edges
    if (this.hasData) {
      this.applyEdgeFilters();
      
      // Clear validation data when switching to filtered views where validation is not available
      if (!this.validationAvailable) {
        this.nodeValidation = null;
        this.nodeValidationStatus.clear();
        this.validationStatus = '';
      }
      
      // Re-validate the currently selected node if there is one and validation is available
      const selectedNode = this.selectedNode;
      if (selectedNode && this.validationAvailable) {
        // Don't await here to avoid blocking the UI
        this.validateNodeTransitions(selectedNode.Id).catch(error => {
          console.warn('Failed to re-validate node after filter change:', error);
        });
      }
    }
  }
  
  private applyEdgeFilters() {
    // Re-process the state data with filters applied
    this.processStateData();
  }

  private shouldHideEdge(edge: any): boolean {
    if (this.hideUnlockedEdges && edge.SaveButton_IsUnlock) {
      return true;
    }
    if (this.hideSpecificRelationships && edge.Name && edge.Name.trim() !== '') {
      return true;
    }
    
    // Check for deleted relationships using multiple methods
    if (this.hideDeletedRelationships) {
      // Method 1: Check if transition name/label contains "deleted"
      if (edge.Name && edge.Name.toLowerCase().includes('deleted')) {
        return true;
      }
      if (edge.label && edge.label.toLowerCase().includes('deleted')) {
        return true;
      }
      
      // Method 2: Check if transition goes TO a deleted state (semantic check)
      if (this.deletedStateIds.has(edge.to)) {
        return true;
      }
    }
    
    return false;
  }

  private updateGraph(nodes: Node[], edges: Edge[]) {
    if (!this.network) return;
    
    
    // Only enable physics for first load or when explicitly needed
    const isFirstLoad = !this.hasData;
    if (isFirstLoad && !this.physicsEnabled) {
      this.network.setOptions({ physics: { enabled: true } });
      this.physicsEnabled = true;
    }
    
    // Transform nodes for vis-network format
    const visNodes = nodes.map(node => {
      let tooltip = `State: ${node.label || node.Name}\\n`;
      tooltip += `Module: ${node.ModuleUniqueId}\\n`;
      tooltip += `Status: ${node.ModuleStatus_Description}\\n`;
      tooltip += `Global Status: ${node.GlobalStatus_Description}\\n`;
      
      if (node.Steps) {
        tooltip += `\\nAvailable Steps:\\n${node.Steps}\\n`;
      }
      
      if (node.Fields) {
        tooltip += `\\nFields:\\n${node.Fields}\\n`;
      }
      
      if (node.RequiredPermissions) {
        tooltip += `\\nRequired Permissions:\\n${node.RequiredPermissions}`;
      }
      
      const nodeType = node.IsInitialState ? 'initial' : node.IsSignOffState ? 'signoff' : 'state';
      const canProgress = this.nodeValidationStatus.get(node.Id) ?? true;
      const nodeColor = canProgress ? this.getNodeColor(nodeType) : '#F44336'; // Red for blocked nodes
      
      
      return {
        id: node.Id,
        label: node.label || node.Name,
        title: tooltip,
        color: nodeColor,
        shape: node.IsInitialState ? 'ellipse' : node.IsSignOffState ? 'box' : 'box',
        size: node.IsInitialState ? 30 : node.IsSignOffState ? 25 : 20,
        borderWidth: node.IsInitialState ? 3 : 2,
        shapeProperties: {
          borderRadius: node.IsSignOffState ? 2 : 6
        },
        originalType: nodeType // Store for validation color updates
      };
    });
    
    // Transform edges for vis-network format
    const visEdges = edges.map(edge => {
      let tooltip = `Transition: ${edge.label || edge.Name}\\n`;
      tooltip += `Description: ${edge.Description || 'No description'}\\n`;
      tooltip += `Approval: ${edge.IsApprovalTransition ? 'Yes' : 'No'}\\n`;
      tooltip += `Rejection: ${edge.IsRejectionTransition ? 'Yes' : 'No'}\\n`;
      
      if (edge.SaveButton_Label) {
        tooltip += `\\nSave Button: ${edge.SaveButton_Label}`;
        tooltip += ` (${edge.SaveButton_Enabled ? 'Enabled' : 'Disabled'})\\n`;
        tooltip += ` Unlock: ${edge.SaveButton_IsUnlock ? 'Yes' : 'No'}\\n`;
      }
      
      if (edge.MandatoryFields) {
        tooltip += `\\nMandatory Fields:\\n${edge.MandatoryFields}\\n`;
      }
      
      if (edge.PreConditionRules) {
        tooltip += `\\nPre-Condition Rules:\\n${edge.PreConditionRules}\\n`;
      }
      
      if (edge.PostConditionValues) {
        tooltip += `\\nPost-Condition Values:\\n${edge.PostConditionValues}\\n`;
      }
      
      if (edge.RequiredPermissions) {
        tooltip += `\\nRequired Permissions:\\n${edge.RequiredPermissions}\\n`;
      }
      
      if (edge.TriggerFields) {
        tooltip += `\\nTrigger Fields:\\n${edge.TriggerFields}`;
      }
      
      // Color coding: Purple for system rules, then approval/rejection colors, then default
      let edgeColor = '#2196F3'; // Default blue
      if (edge.HasSystemRules) {
        edgeColor = '#9C27B0'; // Purple for system rules
      } else if (edge.IsApprovalTransition) {
        edgeColor = '#4CAF50'; // Green for approval
      } else if (edge.IsRejectionTransition) {
        edgeColor = '#F44336'; // Red for rejection
      }
      
      // Determine if edge should be hidden based on filters
      let hidden = false;
      if (!this.showAllEdges) {
        // Special case: If "Hide Named Relationships" is checked AND "Hide Unlocked" is unchecked,
        // only show unlock edges (hide everything that is NOT unlock)
        if (this.hideSpecificRelationships && !this.hideUnlockedEdges) {
          hidden = !edge.SaveButton_IsUnlock;
        }
        // Special case: If "Hide Named Relationships" is checked AND "Hide Deleted" is unchecked,
        // only show deleted edges (hide everything that is NOT deleted)
        else if (this.hideSpecificRelationships && !this.hideDeletedRelationships) {
          const isDeleted = (edge.Name && edge.Name.toLowerCase().includes('deleted')) || 
                           (edge.label && edge.label.toLowerCase().includes('deleted')) ||
                           this.deletedStateIds.has(edge.to); // Also check semantic deletion
          hidden = !isDeleted;
        } else {
          // Standard filtering logic - use the centralized shouldHideEdge method
          hidden = this.shouldHideEdge(edge);
        }
      }
      
      return {
        id: edge.Id,
        from: edge.from,
        to: edge.to,
        label: edge.label || edge.Name,
        title: tooltip,
        color: edgeColor,
        width: edge.HasSystemRules ? 3 : 2, // Thicker lines for system rules
        dashes: edge.HasSystemRules ? false : false, // Could make system rules dashed if needed
        hidden: hidden,
        // Store metadata for filtering
        SaveButton_IsUnlock: edge.SaveButton_IsUnlock,
        Name: edge.Name
      };
    });
    
    const data = {
      nodes: visNodes,
      edges: visEdges
    };
    
    // Store edges for validation purposes
    this.currentEdges = edges;
    
    this.network.setData(data);
  }
  
  private getNodeColor(type?: string): string {
    const colors: { [key: string]: string } = {
      'initial': '#4CAF50',      // Green for initial states
      'signoff': '#FF9800',      // Orange for sign-off states  
      'state': '#2196F3',        // Blue for regular states
      'transition': '#FFAB91',
      'condition': '#C8E6C9',
      'action': '#FFE082',
      'default': '#9E9E9E'       // Grey for default
    };
    
    return colors[type?.toLowerCase() || 'default'] || colors['default'];
  }

  async selectNode(nodeId: number) {
    try {
      // Find the node in our existing state data
      const stateNode = this.stateData.find(s => s.ModuleStatusId === nodeId);
      if (!stateNode) {
        this.errorMessage = `Node ${nodeId} not found in current state data`;
        return;
      }

      // Create a node details object from the state data
      const nodeDetails = {
        Id: stateNode.ModuleStatusId,
        Name: stateNode.Name,
        ModuleUniqueId: stateNode.ModuleId,
        Version: stateNode.Version,
        ModuleStatus_Id: stateNode.ModuleStatusId,
        ModuleStatus_Description: stateNode.ModuleStatus?.Description || '',
        GlobalStatus_Description: stateNode.GlobalStatus?.Description || '',
        IsInitialState: stateNode.ModuleStatus?.IsInitialState || false,
        IsSignOffState: stateNode.IsSignOffState,
        IsPublishable: stateNode.ModuleStatus?.IsPublishable || false
      };

      // Create steps data from available steps
      const steps = stateNode.AvailableSteps?.map((stepName, index) => ({
        Id: index,
        Name: stepName,
        StepTypes: 'Available', // We don't have detailed type info from the state data
        IsRequired: false // We don't have this info from the state data
      })) || [];

      // Create fields data from mandatory fields
      const fields = stateNode.MandatoryOnlyAtStateFields?.map((fieldName, index) => ({
        Id: index,
        Name: fieldName,
        DataType: 'Unknown', // We don't have this info from the state data
        Description: '',
        IsRequired: true, // These are mandatory fields
        IsReadOnly: false,
        IsEditable: true,
        IsAlwaysEditableAtState: false,
        DefaultValue: null
      })) || [];

      this.selectedNode = nodeDetails;
      this.selectedEdge = null;
      this.nodeSteps = steps;
      this.nodeFields = fields;
      this.edgeRules = [];
      
      
      // Validate node transitions by analyzing all outgoing transitions from current graph
      await this.validateNodeTransitions(nodeId);
      
      this.showSidebar = true;
    } catch (error) {
      this.errorMessage = `Failed to load node details: ${error}`;
    }
  }

  async validateNodeTransitions(nodeId: number) {
    try {
      // Find the node in our existing state data
      const stateNode = this.stateData.find(s => s.ModuleStatusId === nodeId);
      if (!stateNode) {
        this.nodeValidation = null;
        this.nodeValidationStatus.set(nodeId, true);
        return;
      }
      
      // Skip validation for nodes with gl:Deleted or gl:Closed status
      if (stateNode.GlobalStatus?.Description === 'gl:Deleted' || stateNode.GlobalStatus?.Description === 'gl:Closed') {
        this.nodeValidation = {
          canProgress: true,
          blockingConditions: [],
          warnings: ['Node has terminal status (gl:Deleted or gl:Closed) - validation skipped'],
          analysisDetails: {
            totalCombinations: 0,
            validCombinations: 0,
            blockedCombinations: 0
          },
          transitionAnalysis: []
        };
        this.nodeValidationStatus.set(nodeId, true);
        return;
      }
      
      // Find outgoing edges for this node from stored edges
      let outgoingEdges = this.currentEdges.filter((edge: any) => edge.from === nodeId);
      
      
      // Filter out hidden edges based on current filter settings
      if (!this.showAllEdges) {
        outgoingEdges = outgoingEdges.filter(edge => !this.shouldHideEdge(edge));
      }
      
      // Now we have access to actual system rules, so we can do proper validation
      const transitionsWithRules: Array<{edge: any, rules: any[]}> = [];
      
      for (const edge of outgoingEdges) {
        const systemRules = edge.systemRules || [];
        transitionsWithRules.push({
          edge: edge,
          rules: systemRules.map(rule => ({
            ...rule,
            RuleType: 'SystemRule',
            FullRuleJson: rule.FullRuleJson || JSON.stringify(rule)
          }))
        });
      }
      
      // Use the validation service for proper validation
      let validation;
      if (transitionsWithRules.length > 0) {
        validation = await this.validationService.validateNodeTransitionsSeparately(
          transitionsWithRules, 
          nodeId, 
          stateNode.ModuleStatusId, 
          this.currentModuleId
        );
      } else {
        validation = {
          canProgress: false,
          blockingConditions: ['No available transitions from this state'],
          warnings: [],
          analysisDetails: {
            totalCombinations: 0,
            validCombinations: 0,
            blockedCombinations: 0
          },
          transitionAnalysis: []
        };
      }
      
      this.nodeValidation = validation;
      
      // Cache the validation status for this node
      this.nodeValidationStatus.set(nodeId, validation.canProgress);
      
      // Update the color of this specific node
      this.updateSingleNodeColor(nodeId, validation.canProgress);
    } catch (error) {
      console.warn('Failed to validate node transitions:', error);
      this.nodeValidation = null;
      this.nodeValidationStatus.set(nodeId, true); // Default to valid if validation fails
    }
  }

  async validateAllNodes() {
    if (!this.network || !this.hasData) {
      return;
    }

    if (!this.dynamicRulesLoaded) {
      this.validationStatus = 'Loading validation rules...';
      await this.reloadValidationRules();
    }

    this.validationInProgress = true;
    this.validationStatus = 'Validating nodes...';

    
    // Clear existing validation status
    this.nodeValidationStatus.clear();

    // Validate each node in our state data
    for (const stateNode of this.stateData) {
      try {
        const nodeId = stateNode.ModuleStatusId;
        
        // Skip validation for nodes with gl:Deleted or gl:Closed status
        if (stateNode.GlobalStatus?.Description === 'gl:Deleted' || stateNode.GlobalStatus?.Description === 'gl:Closed') {
          this.nodeValidationStatus.set(nodeId, true);
          continue;
        }
        
        // Find outgoing edges for this node
        let outgoingEdges = this.currentEdges.filter((edge: any) => edge.from === nodeId);
        
        
        // Filter out hidden edges based on current filter settings
        if (!this.showAllEdges) {
          outgoingEdges = outgoingEdges.filter(edge => !this.shouldHideEdge(edge));
        }
        
        // Use proper validation with actual system rules
        
        const transitionsWithRules: Array<{edge: any, rules: any[]}> = [];
        
        for (const edge of outgoingEdges) {
          const systemRules = edge.systemRules || [];
          transitionsWithRules.push({
            edge: edge,
            rules: systemRules.map(rule => ({
              ...rule,
              RuleType: 'SystemRule',
              FullRuleJson: rule.FullRuleJson || JSON.stringify(rule)
            }))
          });
        }
        
        let canProgress = true;
        if (transitionsWithRules.length > 0) {
          const validation = await this.validationService.validateNodeTransitionsSeparately(
            transitionsWithRules, 
            nodeId, 
            stateNode.ModuleStatusId, 
            this.currentModuleId
          );
          canProgress = validation.canProgress;
        } else {
          canProgress = false; // No transitions available
        }
        
        this.nodeValidationStatus.set(nodeId, canProgress);
      } catch (error) {
        console.warn(`Failed to validate node ${stateNode.ModuleStatusId}:`, error);
        this.nodeValidationStatus.set(stateNode.ModuleStatusId, true); // Default to valid if validation fails
      }
    }

    
    // Force update node colors immediately
    this.validationStatus = 'Updating node colors...';
    this.updateNodeColorsOnly();
    
    const blockedCount = this.getBlockedNodeCount();
    const totalCount = this.stateData.length;
    this.validationStatus = `Validation complete! ${blockedCount} of ${totalCount} nodes blocked.`;
    
    setTimeout(() => {
      this.validationStatus = '';
      this.validationInProgress = false;
    }, 3000);
  }

  private updateNodeColors() {
    if (!this.network) return;

    // Instead of triggering a full graph refresh, just update the graph data
    // The next natural graph update will pick up the validation status
  }

  private updateNodeColorsOnly() {
    if (!this.network) return;

    
    // Get current nodes from the network
    const nodes = this.network.body.data.nodes;
    const nodeUpdates: any[] = [];

    // Update each node's color based on validation status
    nodes.forEach((node: any) => {
      const nodeId = node.id;
      const canProgress = this.nodeValidationStatus.get(nodeId) ?? true;
      
      // Determine node type from stored data or make educated guess
      const nodeType = node.originalType || 'state';
      const newColor = canProgress ? this.getNodeColor(nodeType) : '#F44336';
      
      if (node.color !== newColor) {
        nodeUpdates.push({
          id: nodeId,
          color: newColor
        });
      }
    });

    // Apply the color updates to vis.js network
    if (nodeUpdates.length > 0) {
      const dataSet = this.network.body.data.nodes;
      dataSet.update(nodeUpdates);
    }
  }

  private updateSingleNodeColor(nodeId: number, canProgress: boolean) {
    if (!this.network) return;

    try {
      const dataSet = this.network.body.data.nodes;
      const existingNode = dataSet.get(nodeId);
      
      if (existingNode) {
        const nodeType = existingNode.originalType || 'state';
        const newColor = canProgress ? this.getNodeColor(nodeType) : '#F44336';
        
        
        dataSet.update({
          id: nodeId,
          color: newColor
        });
      }
    } catch (error) {
      console.warn(`Failed to update color for node ${nodeId}:`, error);
    }
  }

  private processSystemRules(rules: any[]): any[] {
    return rules.map(rule => {
      if (rule.RuleType === 'SystemRule' && rule.FullRuleJson) {
        try {
          const jsonData = JSON.parse(rule.FullRuleJson);
          return {
            ...rule,
            ParsedJson: jsonData,
            RuleId: jsonData.Id,
            JsonId: jsonData.Id,
            IsActive: rule.IsActive,
            IsValid: rule.IsValid
          };
        } catch (e) {
          console.warn('Failed to parse rule JSON:', e);
        }
      }
      return rule;
    });
  }

  async selectEdge(edgeId: number) {
    try {
      // Find the edge in our current edges data
      const edge = this.currentEdges.find(e => e.Id === edgeId);
      if (!edge) {
        this.errorMessage = `Edge ${edgeId} not found in current edge data`;
        return;
      }

      // Check if this edge should be hidden based on current filters
      if (!this.showAllEdges && this.shouldHideEdge(edge)) {
        return; // Don't show details for filtered edges
      }

      // Create edge details from existing data
      const edgeDetails = {
        Id: edge.Id,
        Name: edge.Name || edge.label,
        Description: edge.Description || '',
        FromStateName: edge.FromStateName,
        ToStateName: edge.ToStateName,
        IsApprovalTransition: edge.IsApprovalTransition,
        IsRejectionTransition: edge.IsRejectionTransition,
        SaveButton_Label: edge.SaveButton_Label,
        SaveButton_Enabled: edge.SaveButton_Enabled,
        SaveButton_Style: edge.SaveButton_Style,
        SaveButton_IsUnlock: edge.SaveButton_IsUnlock,
        SaveButton_MoveNext: edge.SaveButton_MoveNext
      };

      // Get the actual system rules from the edge data
      const systemRules = edge.systemRules || [];
      const allRules = [];
      
      // Add actual system rules with proper formatting
      systemRules.forEach((rule, index) => {
        
        const formattedRule = {
          ...rule,
          RuleType: 'SystemRule',
          RuleExpression: rule.RuleExpression || rule.ErrorMessage || `System Rule ${index + 1}`,
          IsActive: rule.IsActive !== undefined ? rule.IsActive : true,
          JsonId: rule.JsonId || rule.RuleId || rule.Id,
          FullRuleJson: rule.FullRuleJson || JSON.stringify(rule, null, 2)
        };
        
        allRules.push(formattedRule);
      });
      
      // Add other rule types based on edge data
      if (edge.MandatoryFields) {
        allRules.push({
          RuleType: 'MandatoryField',
          RuleExpression: edge.MandatoryFields,
          ErrorMessage: `Mandatory fields: ${edge.MandatoryFields}`,
          IsActive: true
        });
      }
      
      if (edge.PostConditionValues) {
        allRules.push({
          RuleType: 'PostCondition',
          RuleExpression: 'Post-condition Values',
          ErrorMessage: edge.PostConditionValues,
          IsActive: true
        });
      }
      
      if (edge.RequiredPermissions) {
        allRules.push({
          RuleType: 'RequiredPermission',
          RuleExpression: edge.RequiredPermissions,
          ErrorMessage: `Required permissions: ${edge.RequiredPermissions}`,
          IsActive: true
        });
      }
      
      if (edge.TriggerFields) {
        allRules.push({
          RuleType: 'TriggerField',
          RuleExpression: edge.TriggerFields,
          ErrorMessage: `Trigger fields: ${edge.TriggerFields}`,
          IsActive: true
        });
      }


      this.selectedEdge = edgeDetails;
      this.selectedNode = null;
      this.edgeRules = allRules;
      this.nodeSteps = [];
      this.nodeFields = [];
      this.showSidebar = true;
    } catch (error) {
      this.errorMessage = `Failed to load edge details: ${error}`;
    }
  }

  closeSidebar() {
    this.showSidebar = false;
    this.selectedNode = null;
    this.selectedEdge = null;
    this.nodeSteps = [];
    this.nodeFields = [];
    this.edgeRules = [];
    this.nodeValidation = null;
  }

  openValidationConfig() {
    const selectedNode = this.selectedNode;
    const config = this.validationService.getValidationConfig(this.currentModuleId);
    
    
    if (selectedNode) {
      const nodeSpecificIgnores = this.validationService.getNodeSpecificIgnores(selectedNode.Id, this.currentModuleId);
      
      // Example: Set node-specific ignores for demonstration
      const allAliases = ['TriageSignOff', 'TriageRequiredAlias', 'InvestigationRequiredAlias', 'InvestigationSignOff', 'SignOff1SignOff', 'SignOff2SignOff'];
      const nodeIgnores = prompt(`Enter comma-separated aliases to ignore for node "${selectedNode.Name}" (${selectedNode.Id}):\nAvailable aliases: ${allAliases.join(', ')}\n\nCurrent ignores: ${nodeSpecificIgnores.join(', ')}`, nodeSpecificIgnores.join(', '));
      
      if (nodeIgnores !== null) {
        const ignoreList = nodeIgnores.split(',').map(s => s.trim()).filter(s => s.length > 0);
        this.validationService.setNodeSpecificIgnores(selectedNode.Id, ignoreList, this.currentModuleId);
        
        // Re-validate this node to see the effect in the sidebar
        this.validateNodeTransitions(selectedNode.Id).catch(error => {
          console.warn('Failed to re-validate node after config change:', error);
        });
        
        alert(`Updated node-specific ignores for "${selectedNode.Name}": ${ignoreList.join(', ')}\n\nClick "Validate All Nodes" to update visual indicators.`);
      }
    } else {
      alert('Please select a node first to configure its specific ignore aliases.');
    }
  }

  
  // Helper methods to group edge rules by type
  getPostConditions() {
    return this.edgeRules.filter(rule => rule.RuleType === 'PostCondition');
  }

  getPreConditionRules() {
    return this.edgeRules.filter(rule => rule.RuleType === 'SystemRule');
  }

  getTriggerFields() {
    return this.edgeRules.filter(rule => rule.RuleType === 'TriggerField');
  }

  getRequiredPermissions() {
    return this.edgeRules.filter(rule => rule.RuleType === 'RequiredPermission');
  }

  getRuleTypesString() {
    return this.edgeRules.map(r => r.RuleType).join(', ');
  }

  toggleJsonView(rule: any) {
    rule.showJson = !rule.showJson;
  }

  getSystemRuleIds() {
    return this.getPreConditionRules()
      .map(r => r.JsonId || r.RuleId)
      .filter(id => id)
      .join(', ');
  }

  // Helper method for template to access Object.keys
  getObjectKeys(obj: any): string[] {
    return Object.keys(obj || {});
  }

  private convertStateNodesToNodes(stateNodes: IStateNode[]): Node[] {
    // First pass: identify deleted states by their names
    this.deletedStateIds.clear();
    stateNodes.forEach(state => {
      if (state.Name && state.Name.toLowerCase().includes('deleted')) {
        this.deletedStateIds.add(state.ModuleStatusId);
      }
    });
    
    return stateNodes.map(state => ({
      Id: state.ModuleStatusId,
      Name: state.Name,
      label: state.Name,
      ModuleUniqueId: state.ModuleId,
      Version: state.Version,
      ModuleStatus_Id: state.ModuleStatusId,
      ModuleStatus_Description: state.ModuleStatus?.Description || '',
      GlobalStatus_Description: state.GlobalStatus?.Description || '',
      IsInitialState: state.ModuleStatus?.IsInitialState || false,
      IsSignOffState: state.IsSignOffState,
      IsPublishable: state.ModuleStatus?.IsPublishable || false,
      Steps: state.AvailableSteps?.join(', ') || '',
      Fields: state.MandatoryOnlyAtStateFields?.join(', ') || '',
      RequiredPermissions: ''
    }));
  }

  private convertStateNodesToEdges(stateNodes: IStateNode[]): Edge[] {
    const edges: Edge[] = [];
    const usedEdgeIds = new Set<number>();
    let edgeIdCounter = 1;
    
    // Create a lookup map for state names
    const stateNameMap = new Map<number, string>();
    stateNodes.forEach(state => {
      stateNameMap.set(state.ModuleStatusId, state.Name);
    });
    
    stateNodes.forEach(state => {
      state.StateTransitions?.forEach(transition => {
        // Get the actual system rules for this transition
        const transitionRules = this.getSystemRulesForTransition(transition.PreConditionRules);
        const hasSystemRules = transitionRules.length > 0;
        
        // Build rule text summaries
        const preConditionRulesText = transitionRules
          .map(rule => rule.RuleExpression || rule.ErrorMessage || 'System Rule')
          .join(', ');
        
        // Process mandatory fields from transition data
        const mandatoryFields = transition.Mandatory ? 
          Object.keys(transition.Mandatory).join(', ') : '';
        
        // Process post condition values
        const postConditionValues = transition.PostConditionValues ?
          Object.entries(transition.PostConditionValues)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ') : '';
        
        // Process save button info
        const saveButton = transition.SaveButton;
        
        // Generate unique edge ID
        let edgeId = state.ModuleStatusId * 10000 + transition.ModuleStatusId;
        while (usedEdgeIds.has(edgeId)) {
          edgeId = this.fallbackEdgeIdBase + edgeIdCounter++;
        }
        usedEdgeIds.add(edgeId);
        
        edges.push({
          Id: edgeId,
          from: state.ModuleStatusId,
          to: transition.ModuleStatusId,
          Name: transition.Description,
          label: transition.Description,
          Description: transition.Description,
          FromStateName: state.Name,
          ToStateName: stateNameMap.get(transition.ModuleStatusId) || 'Unknown',
          IsApprovalTransition: transition.IsApprovalTransition,
          IsRejectionTransition: transition.IsRejectionTransition,
          SaveButton_Label: saveButton?.Label || 'Save',
          SaveButton_Enabled: true,
          SaveButton_IsUnlock: saveButton?.IsUnlock || false,
          SaveButton_MoveNext: saveButton?.MoveNext || true,
          SaveButton_Style: saveButton?.Style || 'primary',
          HasSystemRules: hasSystemRules,
          MandatoryFields: mandatoryFields,
          PreConditionRules: preConditionRulesText,
          PostConditionValues: postConditionValues,
          RequiredPermissions: '', // Would need additional data to populate
          TriggerFields: saveButton?.TriggerFields || '',
          // Store the actual rule data for detailed analysis
          systemRules: transitionRules
        });
      });
    });
    
    return edges;
  }
  
  private getSystemRulesForTransition(ruleIds: number[]): any[] {
    if (!ruleIds || !this.tenantService.stateRules) {
      return [];
    }
    
    return this.tenantService.stateRules.filter(rule => 
      ruleIds.includes(rule.Id)
    );
  }

  private async loadDynamicValidationRules() {
    try {
      const dynamicConfig = await this.validationService.loadDynamicValidationConfig(this.currentModuleId);
      
      this.dynamicRulesLoaded = true;
    } catch (error) {
      console.error('Failed to load dynamic validation rules:', error);
      this.dynamicRulesLoaded = false;
    }
  }

  async reloadValidationRules() {
    
    // Clear the cache to force reload
    this.validationService.clearCache();
    this.dynamicRulesLoaded = false;
    
    // Reload dynamic rules
    await this.loadDynamicValidationRules();
    
    // Re-run validation with new rules
    this.validateAllNodes().catch(error => {
      console.warn('Failed to validate all nodes after reloading rules:', error);
    });
  }

  private getBlockedNodeCount(): number {
    let blockedCount = 0;
    this.nodeValidationStatus.forEach((canProgress) => {
      if (!canProgress) {
        blockedCount++;
      }
    });
    return blockedCount;
  }
  
  ngOnDestroy() {
    // Clear any pending timeouts
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }
    
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout);
      this.validationTimeout = null;
    }
    
    // Clean up the network
    if (this.network) {
      this.network.destroy();
      this.network = null;
    }
    
  }
}